import { useState, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { cloneDeep } from "lodash";
import swal from "sweetalert";
import { Box, CircularProgress } from "@mui/material";
import axiosNoAuthInstance from "../../axiosApiNoAuth";
import DashboardMap from "./DashboardMap";
import {
  firmSizeRangeAtom,
  selectedFiltersAtom,
  selectedPlaceAtom,
  selectedSpatialLayerAtom,
  selectedSpatialValueAtom,
  smallBusinessMapColourOnAtom,
  smallBusinessMapIntensityDataAtom,
  smallBusinessMapHighlightAtom,
  smallBusinessMapZoomInAtom,
} from "../../../shared/state/atoms";
import { useGeoJSON } from "../../../shared/hooks";

// Map of Intensity (Small Business) — country-wide percentage map of FTE in the small-business
// firm-size range, rendered at the user's selected spatial layer.
//
// Uses DashboardMap (the same map component as Job Density and Change in Metro). The
// monoColour prop, added in Phase B5.2, lets DashboardMap render in single-hue blue mode for
// district + province layers per PRD. Four spatial-layer modes:
//   - Municipality + cat_b="A" → hex view of selected metro (taxdata_modelname=hex7_fte_firmsizefte)
//   - Municipality + cat_b!="A" → country view of all local munis (allmunicipalities)
//   - District → country view of districts (alldistricts), monoColour=true
//   - Province → country view of provinces (allprovinces), monoColour=true
//
// IMPORTANT: smallBusinessMapIntensityDataAtom is a plain in-memory atom (NOT atomWithStorage)
// because the country-wide polygon data (full SA boundaries) exceeds the localStorage ~5MB
// quota. Persisting to localStorage was the original cause of the QuotaExceededError that
// surfaced as "No Data" for non-hex layers — fixed in B5.x.

function resolveLayerConfig(selectedSpatialLayer, selectedPlace) {
  if (!selectedSpatialLayer || !selectedPlace) return null;
  const layerId = selectedSpatialLayer.id;
  const isMetroMuni = layerId === "municipality" && selectedPlace?.category === "A";

  if (isMetroMuni) {
    return {
      mode: "hex",
      taxdata_modelname: "hex7_fte_firmsizefte",
      spatial: "hex7",
      geoFile: "Metros_hex.geo.json",
      vget_field: "hex7",
      vpoly_field: "hex7",
      monoColour: false,
      metroForViewport: selectedPlace?.name,
      extraParams: { metro: selectedPlace?.name, cat_b: selectedPlace?.cat_b },
    };
  }

  if (layerId === "municipality") {
    return {
      mode: "allmunicipalities",
      taxdata_modelname: "metro_fte_firmsizefte",
      spatial: "allmunicipalities",
      geoFile: "LocalMunicipalities_2016.geo.json",
      vget_field: "cat_b",
      vpoly_field: "CAT_B",
      monoColour: false,
      metroForViewport: undefined,
      extraParams: {},
    };
  }

  if (layerId === "district") {
    return {
      mode: "alldistricts",
      taxdata_modelname: "metro_fte_firmsizefte",
      spatial: "alldistricts",
      geoFile: "DistrictMunicipalities_2018.geo.json",
      vget_field: "place_id",
      vpoly_field: "DISTRICT",
      monoColour: true, // mono per PRD
      metroForViewport: undefined,
      extraParams: {},
    };
  }

  if (layerId === "province") {
    return {
      mode: "allprovinces",
      taxdata_modelname: "metro_fte_firmsizefte",
      spatial: "allprovinces",
      geoFile: "Province_2018.geo.json",
      vget_field: "place_id",
      vpoly_field: "PROVINCE",
      monoColour: true, // mono per PRD
      metroForViewport: undefined,
      extraParams: {},
    };
  }

  return null;
}

// Flatten all coordinates from a GeoJSON geometry (handles Polygon and MultiPolygon).
function flattenCoords(geometry) {
  const coords = [];
  if (!geometry) return coords;
  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach((ring) => ring.forEach((c) => coords.push(c)));
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((poly) => poly.forEach((ring) => ring.forEach((c) => coords.push(c))));
  }
  return coords;
}

// Ray-casting point-in-polygon test for a single ring (array of [lng, lat]).
function pointInRing(lng, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

// Test if a point is inside a GeoJSON geometry (respects holes and MultiPolygon).
function pointInGeometry(lng, lat, geometry) {
  if (!geometry) return false;
  if (geometry.type === "Polygon") {
    // First ring is outer boundary; subsequent rings are holes.
    if (!pointInRing(lng, lat, geometry.coordinates[0])) return false;
    for (let h = 1; h < geometry.coordinates.length; h++) {
      if (pointInRing(lng, lat, geometry.coordinates[h])) return false;
    }
    return true;
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.some((poly) => {
      if (!pointInRing(lng, lat, poly[0])) return false;
      for (let h = 1; h < poly.length; h++) {
        if (pointInRing(lng, lat, poly[h])) return false;
      }
      return true;
    });
  }
  return false;
}

// Find all latitudes where a vertical line at the given longitude intersects the polygon
// edges. Returns an array of intersection latitudes sorted by distance from targetLat.
function verticalIntersections(lng, geometry) {
  const hits = [];
  const processRing = (ring) => {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [x1, y1] = ring[j];
      const [x2, y2] = ring[i];
      if ((x1 <= lng && x2 > lng) || (x2 <= lng && x1 > lng)) {
        const t = (lng - x1) / (x2 - x1);
        hits.push(y1 + t * (y2 - y1));
      }
    }
  };
  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach(processRing);
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((poly) => poly.forEach(processRing));
  }
  return hits;
}

// Compute the centroid (bbox centre) and lat extent of a GeoJSON feature.
// If the bbox centre falls outside the polygon (concave shapes), the latitude
// is adjusted to the nearest polygon boundary along the same longitude.
function computeCentroid(feature) {
  const coords = flattenCoords(feature?.geometry);
  if (coords.length === 0) return null;
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  let cLng = (minLng + maxLng) / 2;
  let cLat = (minLat + maxLat) / 2;

  if (!pointInGeometry(cLng, cLat, feature.geometry)) {
    // Find vertical intersections at the centroid longitude and pick the nearest.
    const hits = verticalIntersections(cLng, feature.geometry);
    if (hits.length > 0) {
      hits.sort((a, b) => Math.abs(a - cLat) - Math.abs(b - cLat));
      // Move slightly inside from the boundary (0.01° ≈ 1km) so the point is interior.
      const nearest = hits[0];
      cLat = nearest > cLat ? nearest - 0.01 : nearest + 0.01;
    }
  }

  return { longitude: cLng, latitude: cLat, minLat, maxLat };
}

// Compute a deck.gl viewState that fits a GeoJSON feature's bounding box with some padding.
// If fixedZoom is provided, uses that zoom level instead of calculating from bbox span
// (gives consistent magnification so relative municipality sizes are visible).
function computeZoomedViewport(feature, fixedZoom) {
  const coords = flattenCoords(feature?.geometry);
  if (coords.length === 0) return null;

  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  const centerLng = (minLng + maxLng) / 2;
  const centerLat = (minLat + maxLat) / 2;

  let zoom;
  if (fixedZoom) {
    zoom = fixedZoom;
  } else {
    const spanLng = maxLng - minLng;
    const spanLat = maxLat - minLat;
    const span = Math.max(spanLng, spanLat);
    // Approximate zoom from the span (degrees). Larger spans → lower zoom.
    // log2(360 / span) gives roughly the right zoom level; subtract a bit for padding.
    zoom = Math.max(4, Math.min(12, Math.log2(360 / span) - 0.5));
  }

  return {
    longitude: centerLng,
    latitude: centerLat,
    zoom,
    minZoom: zoom - 0.5,
    maxZoom: zoom + 2,
    pitch: 0,
    bearing: 0,
  };
}

// Compute a viewport that includes the selected feature and all its immediate
// spatial neighbours. "Neighbours" = any feature whose bounding box overlaps
// an expanded version of the selected feature's bbox (expanded by 10% of the
// selected feature's span in each direction, to catch shared-boundary neighbours).
function computeClusterViewport(selectedFeature, allFeatures) {
  if (!selectedFeature || !allFeatures?.length) return computeZoomedViewport(selectedFeature);

  const selCoords = flattenCoords(selectedFeature.geometry);
  if (selCoords.length === 0) return null;

  let sMinLng = Infinity, sMaxLng = -Infinity, sMinLat = Infinity, sMaxLat = -Infinity;
  for (const [lng, lat] of selCoords) {
    if (lng < sMinLng) sMinLng = lng;
    if (lng > sMaxLng) sMaxLng = lng;
    if (lat < sMinLat) sMinLat = lat;
    if (lat > sMaxLat) sMaxLat = lat;
  }

  // Expand the selected bbox by 10% of its span to catch adjacent features
  const lngPad = (sMaxLng - sMinLng) * 0.1 || 0.1;
  const latPad = (sMaxLat - sMinLat) * 0.1 || 0.1;
  const exMinLng = sMinLng - lngPad;
  const exMaxLng = sMaxLng + lngPad;
  const exMinLat = sMinLat - latPad;
  const exMaxLat = sMaxLat + latPad;

  // Collect bboxes of the selected feature + all overlapping neighbours
  let cMinLng = sMinLng, cMaxLng = sMaxLng, cMinLat = sMinLat, cMaxLat = sMaxLat;

  for (const f of allFeatures) {
    if (f === selectedFeature) continue;
    const coords = flattenCoords(f.geometry);
    if (coords.length === 0) continue;
    let fMinLng = Infinity, fMaxLng = -Infinity, fMinLat = Infinity, fMaxLat = -Infinity;
    for (const [lng, lat] of coords) {
      if (lng < fMinLng) fMinLng = lng;
      if (lng > fMaxLng) fMaxLng = lng;
      if (lat < fMinLat) fMinLat = lat;
      if (lat > fMaxLat) fMaxLat = lat;
    }
    // Check bbox overlap with expanded selection bbox
    if (fMaxLng >= exMinLng && fMinLng <= exMaxLng && fMaxLat >= exMinLat && fMinLat <= exMaxLat) {
      if (fMinLng < cMinLng) cMinLng = fMinLng;
      if (fMaxLng > cMaxLng) cMaxLng = fMaxLng;
      if (fMinLat < cMinLat) cMinLat = fMinLat;
      if (fMaxLat > cMaxLat) cMaxLat = fMaxLat;
    }
  }

  const centerLng = (cMinLng + cMaxLng) / 2;
  const centerLat = (cMinLat + cMaxLat) / 2;
  const span = Math.max(cMaxLng - cMinLng, cMaxLat - cMinLat);
  const zoom = Math.max(4, Math.min(12, Math.log2(360 / span) - 0.5));

  return {
    longitude: centerLng,
    latitude: centerLat,
    zoom,
    minZoom: zoom - 0.5,
    maxZoom: zoom + 2,
    pitch: 0,
    bearing: 0,
  };
}

function SmallBusinessMapIntensity() {
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [selectedSpatialValue] = useAtom(selectedSpatialValueAtom);
  const [selectedFilters] = useAtom(selectedFiltersAtom);
  const [firmSizeRange] = useAtom(firmSizeRangeAtom);
  const [mapData, setMapData] = useAtom(smallBusinessMapIntensityDataAtom);
  const [zoomIn] = useAtom(smallBusinessMapZoomInAtom);
  const [highlightOn] = useAtom(smallBusinessMapHighlightAtom);
  const [colourOn, setColourOn] = useAtom(smallBusinessMapColourOnAtom);

  const [maxValue, setMaxValue] = useState(0);
  const [minValue, setMinValue] = useState(0);
  const [mapDataGrey, setMapDataGrey] = useState(null);
  const [reportKey, setReportKey] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const layerConfig = resolveLayerConfig(selectedSpatialLayer, selectedPlace);
  const { geoData } = useGeoJSON(layerConfig?.geoFile ?? "Metros_hex.geo.json");

  // 1-handle slider; small segment is [0, handle). When inverted, query the medium-large range.
  const handles = firmSizeRange?.smallBusinessMapIntensity?.handles ?? [50];
  const inverted = firmSizeRange?.smallBusinessMapIntensity?.inverted ?? false;
  const firmsize_fte_lower = inverted ? handles[0] : 0;
  const firmsize_fte_upper = inverted ? 2000000 : handles[0];
  const isEmptyRange = firmsize_fte_lower === firmsize_fte_upper;

  // Clear map data when spatial layer changes so stale data doesn't render with wrong zoom
  const prevModeRef = useRef(layerConfig?.mode);
  useEffect(() => {
    if (layerConfig?.mode !== prevModeRef.current) {
      prevModeRef.current = layerConfig?.mode;
      setMapData(null);
      setMapDataGrey(null);
    }
  }, [layerConfig?.mode, setMapData]);

  // Reset Colour toggle to the per-layer default on every layerConfig.mode change so
  // overrides don't survive a spatial-layer switch (per 2nd revert R5 spec).
  useEffect(() => {
    if (layerConfig) {
      setColourOn(!layerConfig.monoColour);
    }
  }, [layerConfig?.mode, setColourOn]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!layerConfig || !geoData || !selectedSpatialValue || !selectedFilters?.singleYear) {
      // Prerequisites not ready yet (geoData still loading, etc.) — keep the spinner
      // visible rather than toggling to false and flashing the map container empty.
      setIsLoading(true);
      return;
    }

    // Empty range — clear data and show message
    if (isEmptyRange) {
      setMapData(null);
      setMapDataGrey(null);
      setIsLoading(false);
      setErrorMessage("No firm size range selected. Adjust the slider to select a range.");
      return;
    }

    // Sanity-check that geoData matches the expected layer (handles useGeoJSON timing race).
    const expectedField = layerConfig.vpoly_field;
    const sample = geoData?.features?.[0]?.properties;
    if (!sample || !(expectedField in sample)) return;

    async function getMapData() {
      setIsLoading(true);
      setErrorMessage(null);

      const vpoly = { type: "FeatureCollection", features: [] };
      const vpolyGrey = { type: "FeatureCollection", features: [] };

      let vmax = 0;
      let vmin = Number.MAX_SAFE_INTEGER;

      try {
        const params = {
          taxdata_modelname: layerConfig.taxdata_modelname,
          spatial: layerConfig.spatial,
          output: "fte",
          year: selectedFilters.singleYear,
          viewas: "Percentage in each area",
          firmsize_fte_lower,
          firmsize_fte_upper,
          ...layerConfig.extraParams,
        };

        const response = await axiosNoAuthInstance.get("/api/map-explorer/", { params });

        if (!response.data || response.data.length === 0) {
          setIsLoading(false);
          setErrorMessage(`No data available for ${selectedFilters.singleYear}`);
          return;
        }

        for (let i = 0; i < response.data.length; i++) {
          for (let j = 0; j < geoData.features.length; j++) {
            if (geoData.features[j].properties[layerConfig.vpoly_field] === response.data[i][layerConfig.vget_field]) {
              const placeName = response.data[i].place_name || "";
              const showPlace = placeName && placeName !== "Not Used" && placeName !== "Not used";
              const tooltipLabel = (displayVal) => {
                const lines = [
                  '<div style="font-size:12px">',
                  `<span style="font-weight:bold">${inverted ? "Medium-Large Business" : "Small Business"}</span>`,
                  `% FTE: ${displayVal}`,
                ];
                if (showPlace) lines.push(`Place: ${placeName}`);
                lines.push('</div>');
                return lines;
              };
              if (typeof response.data[i].display !== "number") {
                const greyFeature = cloneDeep(geoData.features[j]);
                greyFeature.properties.value = "0";
                greyFeature.properties.label = tooltipLabel(response.data[i].display);
                vpolyGrey.features.push(greyFeature);
              } else {
                const feature = cloneDeep(geoData.features[j]);
                feature.properties.value = response.data[i].display;
                if (parseFloat(feature.properties.value) > vmax) vmax = parseFloat(feature.properties.value);
                if (parseFloat(feature.properties.value) < vmin) vmin = parseFloat(feature.properties.value);
                feature.properties.label = tooltipLabel(parseFloat(response.data[i].display.toFixed(2)));
                vpoly.features.push(feature);
              }
              continue;
            }
          }
        }

        if (vpoly.features.length === 0 && vpolyGrey.features.length === 0) {
          setIsLoading(false);
          setErrorMessage(`No data available for ${selectedFilters.singleYear}`);
          return;
        }

        setMaxValue(vmax);
        setMinValue(vmin === Number.MAX_SAFE_INTEGER ? 0 : vmin);
        setMapData(vpoly);
        setMapDataGrey(vpolyGrey);
        setReportKey((k) => k + 1);
        setIsLoading(false);
      } catch (error) {
        if (error?.response?.status === 404 || error?.response == null) {
          setIsLoading(false);
          setErrorMessage("No Data");
        } else {
          swal("Failed", `${error}`, "error", { timer: 4000, buttons: false });
          setIsLoading(false);
        }
      }
    }

    getMapData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSpatialLayer,
    selectedPlace,
    selectedSpatialValue,
    selectedFilters?.singleYear,
    firmsize_fte_lower,
    firmsize_fte_upper,
    geoData,
  ]);

  if (!layerConfig) {
    return (
      <div className="flex">
        <div className="w-full">
          <div className="text-lg px-10 pt-5 font-sans font-bold">
            {selectedSpatialLayer?.name} / {selectedPlace?.name} / <span style={{ color: "#1976d2" }}>Small Business</span>
          </div>
          <div className="text-3xl font-bold px-10 mb-10 font-roboto">Intensity</div>
          <div className="px-10 text-gray-500 italic">Please complete the spatial selection on the left.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <div className="w-full">
        <div className="text-lg px-10 pt-5 font-sans font-bold">
          {selectedSpatialLayer?.name} / {selectedPlace?.name} / <span style={{ color: "#1976d2" }}>Small Business</span>
        </div>
        <div className="text-3xl font-bold px-10 mb-10 font-roboto">
          Intensity {selectedFilters?.singleYear ? selectedFilters.singleYear : ""}
        </div>

        {errorMessage ? (
          <div className="p-20 flex justify-center">{errorMessage}</div>
        ) : (
          <div className="flex">
            <div className="w-[60%] ml-[20%]">
              {isLoading && (
                <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
                  <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
                    <CircularProgress color="success" size="3rem" />
                  </Box>
                </div>
              )}
              {mapData && mapData.features && mapData.features.length > 0 && (
                <DashboardMap
                  key={`intensity-${layerConfig.mode}-${layerConfig.geoFile}-${selectedSpatialValue}-${zoomIn ? "zoomed" : "country"}`}
                  reportKey={reportKey}
                  data={mapData}
                  data_grey={mapDataGrey}
                  metro={layerConfig.metroForViewport}
                  viewAs="Absolute"
                  output="fte"
                  maxValue={maxValue}
                  minValue={minValue}
                  monoColour={!colourOn}
                  customViewport={(() => {
                    if (layerConfig.mode === "hex") return undefined;
                    const baseVp = { longitude: 25, latitude: -29, zoom: 4.55, minZoom: 4.45, maxZoom: 4.65, pitch: 0, bearing: 0 };
                    // Guard against stale geoData from a previous spatial layer
                    const geoReady = geoData?.features?.[0]?.properties?.[layerConfig.vpoly_field] !== undefined;
                    // Province: no zoom toggle, but still apply viewport shift for tooltip visibility below
                    if (zoomIn && geoReady && layerConfig.mode !== "allprovinces") {
                      const selectedFeature = geoData?.features?.find(
                        (f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue
                      );
                      if (layerConfig.mode === "allmunicipalities" || layerConfig.mode === "alldistricts") {
                        // Zoom to show the selected place + all surrounding neighbours
                        const clusterVp = computeClusterViewport(selectedFeature, geoData?.features);
                        return clusterVp || baseVp;
                      }
                      const zoomedVp = computeZoomedViewport(selectedFeature);
                      return zoomedVp || baseVp;
                    }
                    // Shift viewport when the selected place's centroid is near the top or
                    // bottom of the visible area, so the pinned tooltip (top) or the place
                    // itself (bottom) doesn't clip outside the map box.
                    // At zoom ~4.55 the visible lat span is ~18° (±9° from centre).
                    const placeCentroid = computeCentroid(
                      geoData?.features?.find(
                        (f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue
                      )
                    );
                    if (placeCentroid) {
                      const topEdge = baseVp.latitude + 9; // top visible edge
                      const bottomEdge = baseVp.latitude - 7; // bottom visible edge (conservative)
                      const topHeadroom = 5.3; // degrees above the centroid for tooltip + arrow
                      const bottomHeadroom = 1.5; // degrees below the polygon's lowest point
                      let shift = 0;
                      // Top: use centroid (tooltip floats above it)
                      if (placeCentroid.latitude > topEdge - topHeadroom) {
                        shift = placeCentroid.latitude - (topEdge - topHeadroom);
                      }
                      // Bottom: use polygon's southernmost point (ensure full polygon visible)
                      if (placeCentroid.minLat < bottomEdge + bottomHeadroom) {
                        const bottomShift = placeCentroid.minLat - (bottomEdge + bottomHeadroom);
                        // If both edges need shifting, prioritise whichever is larger
                        if (shift === 0 || Math.abs(bottomShift) > Math.abs(shift)) {
                          shift = bottomShift;
                        }
                      }
                      if (shift !== 0) {
                        return { ...baseVp, latitude: baseVp.latitude + shift };
                      }
                    }
                    return baseVp;
                  })()}
                  highlightFeature={
                    highlightOn && layerConfig.mode !== "hex"
                      ? geoData?.features?.find(
                          (f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue
                        ) || null
                      : null
                  }
                  pinnedTooltip={(() => {
                    if (layerConfig.mode === "hex" || !highlightOn) return null;
                    const geoFeature = geoData?.features?.find(
                      (f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue
                    );
                    if (!geoFeature) return null;
                    const centroid = computeCentroid(geoFeature);
                    if (!centroid) return null;
                    // Find the matching data feature to get its display value and place name.
                    const dataFeature =
                      mapData?.features?.find((f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue) ||
                      mapDataGrey?.features?.find((f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue);
                    if (!dataFeature?.properties?.label) return null;
                    // Build plain-text lines for the TextLayer (no HTML).
                    const labels = dataFeature.properties.label.filter(
                      (l) => !l.startsWith("<div") && !l.startsWith("</div")
                    );
                    // Strip any remaining HTML tags from label strings.
                    const text = labels.map((l) => l.replace(/<[^>]*>/g, "")).join("\n");
                    return { ...centroid, text };
                  })()}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SmallBusinessMapIntensity;
